import React, { useCallback } from 'react'
import { Alert } from 'antd'
import { useService } from '@pushly/aqe/lib/hooks'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { AppState } from '../../stores/app'

/**
 * Where the dismissal is remembered. Written unscoped, so dismissing it once settles it for the reader
 * everywhere rather than per domain — the builder is the same builder on every domain they open.
 */
const PREFS_KEY_SEGMENT_BUILDER_BANNER_DISMISSED = 'segments:builder-ux-changed-banner-dismissed'

/**
 * Tells a reader that the conditions builder they are looking at is new, and that their existing
 * segments are not.
 *
 * Placed above the conditions rather than within them, so it reads as something said *about* the
 * builder rather than as part of the segment being edited.
 *
 * The reassurance is deliberately about *existing segments* rather than about segmentation generally,
 * because that is both the thing a reader is actually worried about and the thing that is verifiably
 * true: every stored segment round-trips to the same query, so the audience each one targets is
 * unchanged. Claiming nothing at all had changed would be a promise the builder cannot keep — a
 * frequency row now opens at one visit rather than zero, and identifier and personal-data fields
 * deliberately no longer offer a browsable list of values.
 *
 * Dismissed per reader and never shown again, so it costs a returning user one click rather than
 * becoming furniture they read past.
 */
export function SegmentBuilderNotice() {
    const appState = useService(AppState)
    const userPreferences = useUserPreferences(appState.currentUser?.id, appState.currentDomain?.id)

    const dismissed = userPreferences.get(['extras', PREFS_KEY_SEGMENT_BUILDER_BANNER_DISMISSED], false)

    const dismiss = useCallback(() => {
        userPreferences.setUnscoped(['extras', PREFS_KEY_SEGMENT_BUILDER_BANNER_DISMISSED], true)
    }, [userPreferences])

    if (dismissed) return null

    return (
        <Alert
            type="info"
            // `closeIcon: true` asks for the default icon and is what makes the alert closable at all:
            // an object `closable` without one is not treated as closable. Do not simplify this to
            // `closable` + a top-level `onClose` — both of those are deprecated props.
            closable={{ closeIcon: true, onClose: dismiss }}
            title="Segmentation has a new look"
            description={
                <p style={{ margin: 0 }}>
                    Your workflow just got an upgrade. Building segments is now faster and more intuitive with
                    visual grouping and instant-search value lists. Rest assured, your existing segments
                    continue to target the exact same subscribers.
                </p>
            }
            // No margin: this renders as a sibling of the wells, inside a Flex that already spaces
            // them. Adding one here would double the gap above the conditions.
            style={{ boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.15)' }}
        />
    )
}
